import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MigrationProcessing } from 'src/app/models';
import { DataService, Utilities, WorkflowContextService } from 'src/app/services';
import { UploadResponse } from 'src/app/components/upload/upload.component';
import {
  getClientIdFromRoute,
  normalizeMigrationMetadataValidationResult
} from '../settings-migrate-workflow.models';

type MetadataValidationStatus = {
  isValidating: boolean;
  isValid: boolean;
  errors: string[];
  warnings: string[];
  supportedTypes: string[];
  unsupportedTypes: string[];
} | null;

@Component({
  selector: 'app-settings-migrate-workflow-create',
  standalone: false,
  templateUrl: './settings-migrate-workflow-create.component.html',
  styleUrls: ['./settings-migrate-workflow-create.component.scss']
})
export class SettingsMigrateWorkflowCreateComponent implements OnInit {
  migrationProcessing = new MigrationProcessing();
  uploadKey = '';
  clientId = '';
  loading = true;
  saving = false;
  success = false;
  errorMessage = '';
  metadataValidationStatus: MetadataValidationStatus = null;
  showMetadataValidationDetails = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dataService: DataService,
    public context: WorkflowContextService
  ) {}

  ngOnInit(): void {
    this.uploadKey = Utilities.generateId();
    this.clientId = getClientIdFromRoute(this.route, this.context) || '';

    if (!this.clientId) {
      this.errorMessage = 'Unable to determine the current jurisdiction.';
      this.loading = false;
      return;
    }

    this.migrationProcessing.clientId = this.clientId;
    this.loadJurisdictionInfo();
  }

  get canCreate(): boolean {
    return (
      !this.saving &&
      !!this.migrationProcessing.clientId &&
      !!this.migrationProcessing.sourceSystem?.trim() &&
      !!this.migrationProcessing.metaDataFilePath &&
      !this.metadataValidationStatus?.isValidating &&
      !(this.metadataValidationStatus?.errors.length || 0)
    );
  }

  onMetadataFileUploaded(response: UploadResponse): void {
    this.migrationProcessing.metaDataFilePath = response.path;
    this.validateMetadataFile(response.path);
  }

  onMetadataFileDeleted(): void {
    this.migrationProcessing.metaDataFilePath = null;
    this.metadataValidationStatus = null;
    this.showMetadataValidationDetails = false;
  }

  onApplicationDataFileUploaded(response: UploadResponse): void {
    this.migrationProcessing.applicationDataFilePath = response.path;
  }

  onApplicationDataFileDeleted(): void {
    this.migrationProcessing.applicationDataFilePath = null;
  }

  saveMigrationProcessing(): void {
    if (!this.migrationProcessing.clientId) {
      this.errorMessage = 'Please select a jurisdiction';
      return;
    }

    if (!this.migrationProcessing.sourceSystem?.trim()) {
      this.errorMessage = 'Please enter a source system';
      return;
    }

    if (!this.migrationProcessing.metaDataFilePath) {
      this.errorMessage = 'Please upload a metadata file';
      return;
    }

    if (this.metadataValidationStatus?.isValidating) {
      this.errorMessage = 'Please wait for metadata validation to finish';
      return;
    }

    if ((this.metadataValidationStatus?.errors.length || 0) > 0) {
      this.errorMessage = 'Please fix metadata validation errors before saving';
      this.showMetadataValidationDetails = true;
      return;
    }

    if (!this.canCreate) {
      this.errorMessage =
        'Project name and metadata file are required before creating a migration.';
      return;
    }

    this.saving = true;
    this.success = false;
    this.errorMessage = '';

    this.dataService.saveMigrationProcessing(this.migrationProcessing).subscribe({
      next: saved => {
        if (!saved?.id) {
          this.success = true;
          this.saving = false;
          this.navigateToList();
          return;
        }

        this.dataService.parseMetadataFile(saved.id).subscribe({
          next: result => {
            const normalizedValidation =
              normalizeMigrationMetadataValidationResult({
                errors: result.validationErrors || [],
                warnings: result.validationWarnings || []
              });

            this.metadataValidationStatus = {
              isValidating: false,
              isValid: normalizedValidation.isValid,
              errors: normalizedValidation.errors,
              warnings: normalizedValidation.warnings,
              supportedTypes:
                this.metadataValidationStatus?.supportedTypes || [],
              unsupportedTypes:
                this.metadataValidationStatus?.unsupportedTypes || []
            };

            if (normalizedValidation.errors.length > 0) {
              this.errorMessage = `Metadata validation failed with ${normalizedValidation.errors.length} error(s). Please review the errors below.`;
              this.showMetadataValidationDetails = true;
              this.saving = false;
              return;
            }

            this.success = true;
            this.saving = false;
            setTimeout(() => this.navigateToList(), 1500);
          },
          error: err => {
            this.errorMessage =
              'Migration record created, but metadata parsing failed: ' +
              (err.error?.message || err.message);
            this.success = true;
            this.saving = false;
          }
        });
      },
      error: err => {
        this.errorMessage =
          'Failed to create workflow migration: ' +
          (err.error?.message || err.message);
        this.saving = false;
      }
    });
  }

  cancel(): void {
    this.navigateToList();
  }

  private loadJurisdictionInfo(): void {
    this.loading = true;
    this.errorMessage = '';

    this.dataService.getClient(this.clientId).subscribe({
      next: client => {
        this.migrationProcessing.jurisdiction = client?.name || '';
        this.loading = false;
      },
      error: err => {
        this.errorMessage =
          'Failed to load jurisdiction information: ' +
          (err.error?.message || err.message);
        this.loading = false;
      }
    });
  }

  private validateMetadataFile(filePath: string): void {
    this.metadataValidationStatus = {
      isValidating: true,
      isValid: false,
      errors: [],
      warnings: [],
      supportedTypes: [],
      unsupportedTypes: []
    };

    this.dataService.validateMigrationMetadata(filePath).subscribe({
      next: result => {
        const normalizedValidation =
          normalizeMigrationMetadataValidationResult(result);

        this.metadataValidationStatus = {
          isValidating: false,
          isValid: normalizedValidation.isValid,
          errors: normalizedValidation.errors,
          warnings: normalizedValidation.warnings,
          supportedTypes: normalizedValidation.supportedTypes,
          unsupportedTypes: normalizedValidation.unsupportedTypes
        };
      },
      error: err => {
        this.metadataValidationStatus = {
          isValidating: false,
          isValid: false,
          errors: [
            'Failed to validate metadata: ' +
              (err.error?.message || err.message)
          ],
          warnings: [],
          supportedTypes: [],
          unsupportedTypes: []
        };
      }
    });
  }

  private navigateToList(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }
}
